import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';

import { DxcIconName } from '../dxc.types';

/**
 * Iconografía de interfaz.
 *
 * `dxc-state-icon` cubre los estados ilustrados (vacío, sin GPS, sin timbrar):
 * son dibujos grandes, con animación, uno por situación del producto. Esto es lo
 * otro: el icono pequeño que acompaña a un botón, una fila o un campo.
 *
 * Se dibujan aquí y no se toman de Font Awesome ni de Material por tres razones
 * concretas, todas visibles en la vista de borrador antes de esta migración:
 *
 *  1. **Peso desigual.** FA mezcla familias sólida (`fas`) y de contorno (`far`)
 *     con grosores distintos. En la tarjeta del borrador convivían `fa-check-circle`
 *     sólido y `far fa-file-alt` de contorno: el mismo tamaño nominal y dos pesos
 *     ópticos distintos en la misma tarjeta.
 *  2. **Rejilla ajena.** FA dibuja sobre viewBox de ancho variable (448, 512, 576)
 *     y Material sobre 24 con relleno sólido; ninguno se alinea con el trazo de
 *     2px del resto del sistema, así que un icono junto a un borde `--dxc-border`
 *     se ve más pesado que el borde.
 *  3. **Color.** Los glifos de FA se coloreaban con clases `dxf-text-*`, fuera de
 *     la capa de tokens (R1).
 *
 * Todos comparten rejilla de 24, trazo de 1.75, remates redondos y `currentColor`,
 * así que un icono dentro de un `.dxc-btn--primary` sale blanco sin más, y dentro
 * de una fila `--danger` sale rojo, sin una sola regla de color por sitio.
 */
@Component({
    standalone: true,
    selector: 'dxc-icon',
    templateUrl: './dxc-icon.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcIconComponent {

    @Input() name: DxcIconName = 'info';
    /** Hereda el tamaño del contexto (16 en un botón) salvo que se fije. */
    @Input() size?: number;
    /** Vacío cuando el texto contiguo ya lo describe. */
    @Input() ariaLabel = '';

    @HostBinding('class.dxc-icon') readonly hostClass = true;

    @HostBinding('style.--dxc-icon-size')
    get sizeVar() { return this.size ? `${this.size}px` : null; }

    @HostBinding('attr.role')
    get role() { return this.ariaLabel ? 'img' : null; }

    @HostBinding('attr.aria-label')
    get label() { return this.ariaLabel || null; }

    @HostBinding('attr.aria-hidden')
    get hidden() { return this.ariaLabel ? null : 'true'; }
}
